import { Flex, Stack } from '@mantine/core';
import type { JSX } from 'react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import type { EOnboardingTaskType } from '@feathr/blackbox';
import { ensurePredicateKeys } from '@feathr/blackbox';
import { Alert, Button, List, Modal, Radios, showError } from '@feathr/components';
import { useLocalUrl, useStore } from '@feathr/extender/state';
import { cssVar, getIconForAction } from '@feathr/hooks';

import * as styles from './BuildFirstGroupModal.css';

interface IBuildFirstGroupModalProps {
  onClose: () => void;
  onComplete?: (taskType: EOnboardingTaskType) => void;
}

enum EGroupType {
  EngagedPeople = 'engaged_people',
  Custom = 'custom',
}

function BuildFirstGroupModal({ onClose }: Readonly<IBuildFirstGroupModalProps>): JSX.Element {
  const { t } = useTranslation();
  const { Segments } = useStore();
  const history = useHistory();
  const localUrl = useLocalUrl();
  const [selectedGroupType, setSelectedGroupType] = useState<EGroupType>(EGroupType.EngagedPeople);

  function handleGroupTypeChange(value?: string): void {
    if (value) {
      setSelectedGroupType(value as EGroupType);
    }
  }

  async function handleCreate(): Promise<void> {
    if (selectedGroupType === EGroupType.EngagedPeople) {
      try {
        const segment = Segments.create({
          name: 'Engaged people',
          predicates: [
            ensurePredicateKeys({
              kind: 'attribute',
              attr_against: 'date_last_seen',
              attr_type: 'date',
              comparison: 'gt_date_r',
              value: -90,
              unit: 'days',
              group: [],
            }),
          ],
          is_intelligent: true,
          read_only: true,
        });
        await Segments.add(segment);
        history.push(`${localUrl('/data/segments')}?onboarding=true`);
      } catch (error) {
        showError(error, t);
      } finally {
        onClose();
      }
    } else {
      history.push(`${localUrl('/data/explore/all/people')}?onboarding=true`);
    }
  }

  const recommendedSuffix = (
    <Flex align={'center'} gap={cssVar('--spacing-2')}>
      {getIconForAction('magic')}
      <span className={styles.suffix}>{t('Recommended for you')}</span>
    </Flex>
  );

  return (
    <Modal
      description={t('Define who you want to target')}
      onClose={onClose}
      opened={true}
      rightActions={
        <>
          <Button onClick={onClose}>{t('Cancel')}</Button>
          <Button onClick={handleCreate} type={'primary'}>
            {t('Create')}
          </Button>
        </>
      }
      title={t('Build a group')}
    >
      <Flex direction={'column'} gap={cssVar('--spacing-4')}>
        <div>
          <Radios
            label={t('Type')}
            layout={'block'}
            onChange={handleGroupTypeChange}
            options={[
              {
                id: EGroupType.EngagedPeople,
                name: t('Engaged people'),
                description: t('Your most recently seen and tracked people'),
                suffix: recommendedSuffix,
                theme: 'violet',
              },
              {
                id: EGroupType.Custom,
                name: t('Custom'),
                description: '',
              },
            ]}
            orientation={'vertical'}
            value={selectedGroupType}
          />
        </div>

        <Alert
          description={
            <Stack gap={4}>
              <div>
                {t(
                  'Your Super Pixel needs time to collect visitor data before creating effective audience groups.',
                )}
              </div>
              <List hasBullets>
                <List.Item>
                  {t(
                    'Timeline varies based on your website traffic, pixel placement, and visitor patterns.',
                  )}
                </List.Item>
                <List.Item>
                  {t(
                    'Allow up to 7-14 days of data collection, with higher-traffic sites building audiences faster.',
                  )}
                </List.Item>
              </List>
              <div>
                {/* TODO: Add support link - Handle in Cleanup #5536 */}
                <a href={'/'}>{t('Contact support')}</a>
                <span> {t('for personalized guidance on your strategy.')}</span>
              </div>
            </Stack>
          }
          title={t('Audience Building Takes Time')}
          type={'warning'}
        />
      </Flex>
    </Modal>
  );
}

export default BuildFirstGroupModal;
